import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Button } from '../Button';
import {
  AngleDownIcon,
  LogoutIcon,
  AngleUpIcon,
  UsersIcon,
} from '../../helpers/icons';
import { useAssets } from '../../hooks/useAssets';
import { logoutUserEvent } from '../../helpers/events';
import { useAuth, useSidebarCollapse } from '../../zustand';
// import { sidebarTabs } from './constants';
import { capitalizeFirstLetter } from '../../helpers/functions';
import { sidebarIcons } from '../../helpers/globalConstants';

/**
 * Menu labels, by access-control name. These win over the label the server
 * sends, which only reaches the browser at login -- see the note where they
 * are applied.
 */
const MENU_LABELS = {
  general_settings: 'Company Settings',
};

export default function Sidebar() {
  const { role, accessControl, logo, companyName } = useAuth((s) => s);
  const { openSidebar } = useSidebarCollapse((s) => s);
  const { Logo: defaultLogo } = useAssets();
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const isSidebarOpen = openSidebar || isSidebarHovered;

  return (
    <aside
      className={`mainSideBar d--flex flex--column w--full h--full bg--white radius--sm p-t--md p-b--lg gap--md box-shadow--xs ${
        openSidebar ? 'expanded' : 'collapsed'
      }`}
      onMouseEnter={() => !openSidebar && setIsSidebarHovered(true)}
      onMouseLeave={() => !openSidebar && setIsSidebarHovered(false)}
    >
      <div className="d--flex text--primary p-l--lg p-r--md font--3xl font--500 sidebarlogo">
        <h3 className="font--sm text--secondary d--flex align-items--center gap--sm">
          <div className="w-min--32 h-min--32 m-l--xs">
            <img src={logo || defaultLogo} alt="Logo" width={32} height={32} />
          </div>
          <span className="navlinkLabelLogo white-space--nowrap m-l--sm">
            {companyName || `${capitalizeFirstLetter(role)} Login`}
          </span>
        </h3>
      </div>
      <div className="d--flex flex--column gap--xs w--full h--full m-t--xs overflow-y--auto">
        {accessControl &&
          accessControl?.map(
            (
              {
                label,
                name: path,
                'sub-item': subItem,

                // type,
                // list = [],
                // divider = false,
                access,
                // page,
              },
              index,
            ) => {
              let Icon = sidebarIcons[path.replace(/_/g, '-')] || UsersIcon;
              // Access control is fetched at login and kept in local storage,
              // so a label renamed on the server would not reach anyone until
              // they signed in again. This wins over it.
              label = MENU_LABELS[path] || label;
              if (label === 'divider') {
                return (
                  <div
                    className="h-min--1 bg--black-100 w--full m-t--xs m-b--xs"
                    key={index}
                  ></div>
                );
              }

              if (subItem && subItem?.length > 0) {
                return (
                  <Collapse
                    key={path}
                    {...{ subItem, label, Icon, isSidebarOpen }}
                  />
                );
              }
              return (
                <NavLink
                  key={index}
                  to={
                    path === 'general_settings'
                      ? accessControl?.[index]?.['sub-tab']?.[0]?.name ===
                        'general'
                        ? 'settings'
                        : `settings/${accessControl?.[index]?.['sub-tab']?.[0]?.name}`
                      : path.replace(/_/g, '-')
                  }
                  className={({ isActive }) =>
                    `${
                      isActive ? 'bg--black-50 text--primary' : 'text--black'
                    }  d--flex align-items--center justify-content--start d--flex gap--md ${
                      isSidebarOpen ? 'p-l--lg' : 'p-l--xl'
                    } p-r--md h-min--40 font--sm navLink white-space--nowrap`
                  }
                >
                  <div
                    className="d--flex"
                    title={!isSidebarOpen ? label : null}
                  >
                    <Icon width={18} height={18} />
                  </div>
                  <span className="navlinkLabel">{label}</span>
                </NavLink>
              );
            },
          )}
      </div>
      <div className="d--flex p-l--lg p-r--md p-b--sm">
        <Button
          btnClasses="d--flex align-items--center justify-content--start gap--sm c--pointer font--400 logOutBtn p-l--sm"
          type="button"
          variant="transparent"
          icon={<LogoutIcon width={18} />}
          color="danger"
          onClick={() => document.dispatchEvent(logoutUserEvent)}
        >
          <span className="navlinkLabel">Logout</span>
        </Button>
      </div>
    </aside>
  );
}

const Collapse = ({ subItem, label, Icon, isSidebarOpen }) => {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();

  const isAnyChildActive = useMemo(() => {
    const childPaths = (subItem || [])
      .map(({ name }) => (name ? `/${name.replace(/_/g, '-')}` : null))
      .filter(Boolean);
    return childPaths.some((p) => pathname.includes(p));
  }, [pathname, subItem]);

  useEffect(() => {
    if (isAnyChildActive && isSidebarOpen) setShow(true);
  }, [isAnyChildActive, isSidebarOpen]);

  useEffect(() => {
    if (!isSidebarOpen) setShow(false);
  }, [isSidebarOpen]);

  return (
    <div
      // onClick={(e) => {
      //   e.stopPropagation();
      //   setShow((p) => !p);
      // }}
      className="d--flex flex--column dropMenu position--relative"
    >
      <div
        className={`d--flex align-items--center justify-content--between h-min--40 font--sm d--flex gap--sm p-r--md c--pointer dropLink ${
          isAnyChildActive ? 'bg--black-50 text--primary' : 'text--black'
        } ${isSidebarOpen ? 'p-l--lg' : 'p-l--xl'}`}
        onClick={(e) => {
          // e.stopPropagation();
          setShow((p) => !p);
        }}
      >
        <div
          className="d--flex gap--sm align-items--center navLink"
          style={{ marginRight: 'auto' }}
        >
          <div
            className="w-min--20 h-min--20 d--flex align-items--center justify-content--center"
            title={!isSidebarOpen ? label : null}
            aria-label={label}
          >
            {Icon && <Icon width={18} height={18} />}
          </div>
          <span className="navlinkLabel">{label}</span>
        </div>
        <div className="w-min--8 w-max--8">
          {!show ? (
            <AngleDownIcon width={18} height={18} />
          ) : (
            <AngleUpIcon width={18} height={18} />
          )}
        </div>
      </div>
      {show ? (
        <div className="w--full bg--black-50 p--sm radius--sm gap--xs d--flex flex--column dropList">
          {subItem?.length &&
            subItem?.map(({ label, name: path }, index) => {
              return (
                <NavLink
                  key={index}
                  to={path?.replace(/_/g, '-')}
                  className={({ isActive }) =>
                    `${
                      isActive ? 'bg--black-50 text--primary' : 'text--black'
                    }  d--flex align-items--center justify-content--start d--flex gap--sm p-l--lg p-r--md h-min--40 font--sm navLink white-space--nowrap`
                  }
                >
                  <div
                    className="w-min--20 h-min--20"
                    // title={!openSidebar ? list?.title : null}
                  >
                    {/* <Icon width={18} height={18} /> */}
                  </div>
                  <span className="navlinkLabel">{label}</span>
                </NavLink>

                // <div
                //   className="d--flex align-items--center justify-content--start d--flex p-l--md p-r--md h-min--32 font--sm text--black c--pointer dropItems radius--sm"
                //   key={index}
                //   onClick={() => navigate(`${path?.replace(/_/g, '-')}`)}
                // >
                //   {label}
                // </div>
              );
            })}
        </div>
      ) : null}
    </div>
  );
};
